/**
 * ChangePassword Component
 * Allow logged-in users to change their password
 *
 * MIGRATED: Custom CSS → Tailwind CSS (Mobile-First)
 */

import React, { useState } from 'react';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { userManagementAPI } from '@/services/api/userManagement';
import { Button } from '../ui/button';

import { useAppTranslation } from '@/contexts/TranslationContext';
export const ChangePassword: React.FC = () => {
  const { t } = useAppTranslation();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const getPasswordStrength = (password: string) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^a-zA-Z\d]/.test(password)) score++;

    if (score <= 1) return { score, label: 'Weak', color: '#dc2626' };
    if (score <= 3) return { score, label: 'Fair', color: '#ea580c' };
    if (score <= 4) return { score, label: 'Good', color: '#ca8a04' };
    return { score, label: 'Strong', color: '#16a34a' };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (formData.newPassword.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (formData.currentPassword === formData.newPassword) {
      setError('New password must be different from current password');
      return;
    }

    try {
      setSubmitting(true);
      await userManagementAPI.changePassword(
        formData.currentPassword,
        formData.newPassword
      );
      setSuccess(true);
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err: any) {
      console.error('Password change failed:', err);
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 md:p-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Lock className="w-8 h-8 text-blue-600 dark:text-blue-400 flex-shrink-0" />
        <div>
          <h2 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white">{t('auth.change_password', 'Change Password')}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('auth.update_your_account_password', 'Update your account password')}</p>
        </div>
      </div>

      {/* Success Message */}
      {success && (
        <div className="flex items-center gap-2 p-3 md:p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-lg text-green-700 dark:text-green-400 mb-5 text-sm md:text-base">
          <CheckCircle className="w-4 h-4 flex-shrink-0" />
          <span>{t('auth.password_changed_successfully', 'Password changed successfully!')}</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-3 md:p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-700 dark:text-red-400 mb-5 text-sm md:text-base">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Current Password */}
        <div>
          <label htmlFor="currentPassword" className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <Lock className="w-4 h-4" />
            <span>{t('auth.current_password', 'Current Password')}</span>
          </label>
          <div className="relative">
            <input
              id="currentPassword"
              type={showPasswords.current ? 'text' : 'password'}
              value={formData.currentPassword}
              onChange={(e) => setFormData(prev => ({ ...prev, currentPassword: e.target.value }))}
              placeholder={t('auth.enter_your_current_password', 'Enter your current password')}
              required
              disabled={submitting}
              className="w-full px-3 pr-10 py-2.5 md:py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <button
              type="button"
              onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 p-1 transition-colors"
            >
              {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* New Password */}
        <div>
          <label htmlFor="newPassword" className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <Lock className="w-4 h-4" />
            <span>{t('auth.new_password', 'New Password')}</span>
          </label>
          <div className="relative">
            <input
              id="newPassword"
              type={showPasswords.new ? 'text' : 'password'}
              value={formData.newPassword}
              onChange={(e) => setFormData(prev => ({ ...prev, newPassword: e.target.value }))}
              placeholder={t('auth.enter_a_strong_new_password', 'Enter a strong new password')}
              required
              disabled={submitting}
              className="w-full px-3 pr-10 py-2.5 md:py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <button
              type="button"
              onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 p-1 transition-colors"
            >
              {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {/* Password Strength Indicator */}
          {formData.newPassword && (
            <div className="flex items-center gap-3 mt-2">
              <div className="flex-1 h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full transition-all duration-300"
                  style={{
                    width: `${(passwordStrength.score / 5) * 100}%`,
                    backgroundColor: passwordStrength.color
                  }}
                />
              </div>
              <span className="text-xs md:text-sm font-medium min-w-[50px]" style={{ color: passwordStrength.color }}>
                {passwordStrength.label}
              </span>
            </div>
          )}
        </div>

        {/* Confirm New Password */}
        <div>
          <label htmlFor="confirmPassword" className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <Lock className="w-4 h-4" />
            <span>{t('auth.confirm_new_password', 'Confirm New Password')}</span>
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              type={showPasswords.confirm ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
              placeholder={t('auth.reenter_your_new_password', 'Re-enter your new password')}
              required
              disabled={submitting}
              className="w-full px-3 pr-10 py-2.5 md:py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm md:text-base focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all disabled:bg-gray-50 dark:disabled:bg-gray-700 disabled:cursor-not-allowed bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <button
              type="button"
              onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 p-1 transition-colors"
            >
              {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
            <small className="block mt-1.5 text-xs text-red-600 dark:text-red-400">{t('auth.passwords_do_not_match', 'Passwords do not match')}</small>
          )}
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          variant="primary"
          fullWidth
          disabled={submitting || formData.newPassword !== formData.confirmPassword}
          loading={submitting}
          className="mt-2"
        >
          {submitting ? 'Changing Password...' : 'Change Password'}
        </Button>
      </form>
    </div>
  );
};
